<template>
	<div class="top-gainer-chart h-6 w-[64px]">
		<LineChart
			:data="chartData"
			:options="options"
		/>
	</div>
</template>
<script setup lang="ts">
import { computed, defineProps } from "vue";

import "chartjs-adapter-date-fns";
import { Line } from "vue-chartjs";
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend,
	TimeScale,
	Filler
} from "chart.js";

ChartJS.register(
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend,
	TimeScale,
	Filler
);

const props = defineProps({
	currentNetChange: {
		type: Number,
		default: 0
	},
	data: {
		type: Object,
		required: true
	}
});

const POSITIVE = "#01b885";
const NEGATIVE = "#ff5252";
const POSITIVE_RGB = "1, 184, 133";
const NEGATIVE_RGB = "255, 82, 82";
const chartData = computed(() => ({
	labels: props.data.labels,
	datasets: [
		{
			data: props.data.prices,
			borderColor: props.currentNetChange < 0 ? NEGATIVE : POSITIVE,
			// scriptable backgroundColor:
			backgroundColor: (ctx: any) => {
				const chart = ctx.chart;
				const { ctx: canvas, chartArea } = chart;
				if (!chartArea) return null; // chartArea is only set after layout
				const gradient = canvas.createLinearGradient(
					0,
					chartArea.top,
					0,
					chartArea.bottom
				);
				gradient.addColorStop(
					0,
					props.currentNetChange < 0
						? `rgba( ${NEGATIVE_RGB}, 0.3)`
						: `rgba(${POSITIVE_RGB}, 0.3)`
				);
				gradient.addColorStop(
					1,
					props.currentNetChange < 0
						? `rgba( ${NEGATIVE_RGB}, 0)`
						: `rgba(${POSITIVE_RGB}, 0)`
				);
				return gradient;
			},
			fill: "start", // ← tells Chart.js to fill from the line down to the x-axis
			pointRadius: (ctx: any) =>
				ctx.dataIndex === ctx.dataset.data.length - 1 ? 3 : 0,
			pointBackgroundColor: props.currentNetChange < 0 ? NEGATIVE : POSITIVE,
			tension: 0.3
		}
	]
}));

const options = {
	responsive: true,
	maintainAspectRatio: false,
	elements: {
		point: {
			borderWidth: 0
		}
	},
	scales: {
		x: {
			display: false,
			grid: {
				display: false
			}
		},
		y: {
			display: false,
			grid: {
				display: false
			}
		}
	},
	plugins: {
		legend: { display: false },
		tooltip: { enabled: false }
	}
};

const LineChart = Line;
</script>

<style lang="scss" scoped></style>
